import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { I18nService } from '../../../core/i18n';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import type {
  ConnectWhatsAppRequest,
  WhatsAppApiErrorBody,
  WhatsAppConnectionUiState,
} from '../models/whatsapp-connection.types';
import { FacebookSdkLoader, type FacebookLoginResponse } from '../services/facebook-sdk.loader';
import { WhatsAppConnectionService } from '../services/whatsapp-connection.service';

const CONNECT_TIMEOUT_MS = 60_000;
const I18N_NAMESPACE = 'settings.communication.whatsapp.connection';

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  '400:WhatsApp_Connect_Code_Invalid': 'errors.codeInvalid',
  '400:WhatsApp_Connect_Code_Required': 'errors.generic',
  '400:WhatsApp_Connect_WabaId_Required': 'errors.missingData',
  '400:WhatsApp_Connect_PhoneNumberId_Required': 'errors.missingData',
  '401:*': 'errors.sessionExpired',
  '409:WhatsApp_Connection_In_Progress': 'errors.connectionInProgress',
  '409:WhatsApp_PhoneNumber_Already_In_Use': 'errors.phoneInUse',
  '409:WhatsApp_Register_TwoStep_Conflict': 'errors.registerTwoStep',
  '409:WhatsApp_Not_Connected': 'errors.registerNotConnected',
  '502:*': 'errors.metaUnavailable',
};
const FALLBACK_ERROR_KEY = 'errors.generic';

@Injectable({ providedIn: 'root' })
export class WhatsAppConnectionStore {
  private readonly api = inject(WhatsAppConnectionService);
  private readonly sdkLoader = inject(FacebookSdkLoader);
  private readonly i18n = inject(I18nService);
  private readonly toast = inject(ToastService);

  private readonly uiStateSignal = signal<WhatsAppConnectionUiState>('loading');
  private readonly displayPhoneNumberSignal = signal<string | null>(null);
  private readonly connectedAtSignal = signal<string | null>(null);
  private readonly errorMessageSignal = signal<string | null>(null);
  private readonly disconnectingSignal = signal(false);
  private readonly registeredSignal = signal(false);
  private readonly registeredAtSignal = signal<string | null>(null);
  private readonly registeringSignal = signal(false);

  readonly uiState = this.uiStateSignal.asReadonly();
  readonly displayPhoneNumber = this.displayPhoneNumberSignal.asReadonly();
  readonly connectedAt = this.connectedAtSignal.asReadonly();
  readonly errorMessage = this.errorMessageSignal.asReadonly();
  readonly disconnecting = this.disconnectingSignal.asReadonly();
  readonly registered = this.registeredSignal.asReadonly();
  readonly registeredAt = this.registeredAtSignal.asReadonly();
  readonly registering = this.registeringSignal.asReadonly();

  private pendingCode: string | null = null;
  private pendingSessionInfo: { wabaId: string; phoneNumberId: string } | null = null;
  private timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;

  async loadStatus(): Promise<void> {
    this.uiStateSignal.set('loading');
    this.errorMessageSignal.set(null);
    try {
      const response = await firstValueFrom(this.api.getStatus());
      if (response.connected) {
        this.displayPhoneNumberSignal.set(response.displayPhoneNumber);
        this.connectedAtSignal.set(response.connectedAt);
        this.registeredSignal.set(response.registered);
        this.registeredAtSignal.set(response.registeredAt);
        this.uiStateSignal.set('connected');
      } else {
        this.displayPhoneNumberSignal.set(null);
        this.connectedAtSignal.set(null);
        this.registeredSignal.set(false);
        this.registeredAtSignal.set(null);
        this.uiStateSignal.set('not-connected');
      }
    } catch (err) {
      this.handleError(err);
    }
  }

  async startConnection(): Promise<void> {
    if (!environment.metaAppId || !environment.metaEmbeddedSignupConfigId) {
      this.errorMessageSignal.set(this.translate('errors.notConfigured'));
      this.uiStateSignal.set('error');
      return;
    }

    this.clearPendingConnection();
    this.errorMessageSignal.set(null);
    this.uiStateSignal.set('connecting');

    let sdk;
    try {
      sdk = await this.sdkLoader.load(environment.metaAppId);
    } catch {
      this.clearPendingConnection();
      this.errorMessageSignal.set(this.translate('errors.sdkLoadFailed'));
      this.uiStateSignal.set('error');
      return;
    }

    this.messageListener = (event: MessageEvent) => this.handleMessage(event);
    window.addEventListener('message', this.messageListener);

    this.timeoutHandle = setTimeout(() => this.onTimeout(), CONNECT_TIMEOUT_MS);

    sdk.login((response) => this.onFbLoginCallback(response), {
      config_id: environment.metaEmbeddedSignupConfigId,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: '',
        sessionInfoVersion: '3',
      },
    });
  }

  cancelConnection(): void {
    this.clearPendingConnection();
    this.uiStateSignal.set('not-connected');
    this.errorMessageSignal.set(null);
  }

  dismissError(): void {
    this.clearPendingConnection();
    this.errorMessageSignal.set(null);
    this.uiStateSignal.set('not-connected');
  }

  async disconnect(): Promise<void> {
    this.disconnectingSignal.set(true);
    try {
      await firstValueFrom(this.api.disconnect());
      this.finishDisconnectSuccess();
    } catch (err) {
      if (this.isAlreadyDisconnected(err)) {
        this.finishDisconnectSuccess();
        return;
      }
      this.handleError(err);
    } finally {
      this.disconnectingSignal.set(false);
    }
  }

  async register(): Promise<void> {
    this.registeringSignal.set(true);
    try {
      const response = await firstValueFrom(this.api.register());
      this.registeredSignal.set(true);
      this.registeredAtSignal.set(response.registeredAt);
      this.toast.success(this.translate('states.connected.registerSuccess'));
    } catch (err) {
      this.handleRegisterError(err);
    } finally {
      this.registeringSignal.set(false);
    }
  }

  cleanup(): void {
    this.clearPendingConnection();
  }

  private finishDisconnectSuccess(): void {
    this.displayPhoneNumberSignal.set(null);
    this.connectedAtSignal.set(null);
    this.registeredSignal.set(false);
    this.registeredAtSignal.set(null);
    this.uiStateSignal.set('not-connected');
    this.toast.success(this.translate('states.connected.disconnectSuccess'));
  }

  private handleRegisterError(err: unknown): void {
    const key = this.resolveErrorKey(err);
    this.toast.show(this.translate(key), 'error');
  }

  private isAlreadyDisconnected(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    if (err.status !== 409) return false;
    const body = (err.error ?? {}) as WhatsAppApiErrorBody;
    return body.errorKey === 'WhatsApp_Not_Connected';
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.isTrustedOrigin(event.origin)) return;

    let data: unknown;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    if (!data || typeof data !== 'object') return;
    const payload = data as {
      type?: string;
      event?: string;
      data?: { waba_id?: string; phone_number_id?: string };
    };
    if (payload.type !== 'WA_EMBEDDED_SIGNUP') return;
    if (payload.event !== 'FINISH') return;

    const wabaId = payload.data?.waba_id;
    const phoneNumberId = payload.data?.phone_number_id;
    if (!wabaId || !phoneNumberId) return;

    this.pendingSessionInfo = { wabaId, phoneNumberId };
    this.tryDispatchConnect();
  }

  private isTrustedOrigin(origin: string): boolean {
    if (!origin) return false;
    try {
      const url = new URL(origin);
      if (url.protocol !== 'https:') return false;
      return url.hostname === 'www.facebook.com' || url.hostname.endsWith('.facebook.com');
    } catch {
      return false;
    }
  }

  private onFbLoginCallback(response: FacebookLoginResponse): void {
    const code = response?.authResponse?.code;
    if (code) {
      this.pendingCode = code;
      this.tryDispatchConnect();
      return;
    }
    if (this.uiStateSignal() === 'connecting') {
      this.cancelConnection();
    }
  }

  private tryDispatchConnect(): void {
    if (!this.pendingCode || !this.pendingSessionInfo) return;

    const payload: ConnectWhatsAppRequest = {
      authorizationCode: this.pendingCode,
      wabaId: this.pendingSessionInfo.wabaId,
      phoneNumberId: this.pendingSessionInfo.phoneNumberId,
    };

    this.clearPendingConnection();

    firstValueFrom(this.api.connect(payload))
      .then((response) => {
        this.displayPhoneNumberSignal.set(response.displayPhoneNumber);
        this.connectedAtSignal.set(response.connectedAt);
        this.registeredSignal.set(false);
        this.registeredAtSignal.set(null);
        this.uiStateSignal.set('connected');
      })
      .catch((err) => this.handleError(err));
  }

  private onTimeout(): void {
    this.clearPendingConnection();
    this.errorMessageSignal.set(this.translate('errors.timeout'));
    this.uiStateSignal.set('error');
  }

  private clearPendingConnection(): void {
    if (this.timeoutHandle !== null) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    this.pendingCode = null;
    this.pendingSessionInfo = null;
  }

  private handleError(err: unknown): void {
    this.clearPendingConnection();
    const key = this.resolveErrorKey(err);
    this.errorMessageSignal.set(this.translate(key));
    this.uiStateSignal.set('error');
  }

  private resolveErrorKey(err: unknown): string {
    if (!(err instanceof HttpErrorResponse)) return FALLBACK_ERROR_KEY;
    const body = (err.error ?? {}) as WhatsAppApiErrorBody;
    const firstErr = body.errors?.[0];
    const legacyKey = firstErr && typeof firstErr === 'object' ? firstErr.key : undefined;
    const errorKey = body.errorKey ?? legacyKey ?? '*';
    const specific = ERROR_MESSAGE_KEYS[`${err.status}:${errorKey}`];
    if (specific) return specific;
    const wildcard = ERROR_MESSAGE_KEYS[`${err.status}:*`];
    if (wildcard) return wildcard;
    return FALLBACK_ERROR_KEY;
  }

  private translate(shortKey: string): string {
    return this.i18n.t(`${I18N_NAMESPACE}.${shortKey}`);
  }
}
